/**
 * Routing.
 *
 * Three gates, in order: signed in at all, registered, then the app. Each one
 * is a real state a vendor lands in — a verified number with no vendor record
 * is what a printer who got interrupted halfway through signup comes back to,
 * and dropping them at the OTP screen again is how they never finish.
 */
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { I18nProvider } from './lib/i18n';
import { SessionProvider, useSession } from './lib/session';
import { Layout, creatorOnly, doesWork, ownsMedia } from './components/Layout';
import { Spinner } from './components/Primitives';
import { SignInPage } from './pages/SignIn';
import { RegisterPage } from './pages/Register';
import { JobsPage } from './pages/Jobs';
import { QuotePage } from './pages/Quote';
import { ProofPage } from './pages/Proof';
import { PayoutsPage } from './pages/Payouts';
import { ListingsPage } from './pages/Listings';
import { ProfilePage } from './pages/Profile';
import { ProfileEditPage } from './pages/ProfileEdit';
import { TaxBankPage } from './pages/TaxBank';
import { ServicesPage } from './pages/Services';
import { BookingsPage } from './pages/Bookings';
import { OrdersPage } from './pages/Orders';
import { ReportsPage } from './pages/Reports';
import { TeamPage } from './pages/Team';
import { ConnectPage } from './pages/Connect';
import { InvitePage } from './pages/Invite';
import { CreatorHomePage } from './pages/CreatorHome';
import { CreatorKitPage } from './pages/CreatorKit';
import { CreatorPackagesPage } from './pages/CreatorPackages';
import { CreatorDealsPage } from './pages/CreatorDeals';
import { CreatorDealPage } from './pages/CreatorDeal';
import { CreatorOpportunitiesPage, CreatorOpportunityPage } from './pages/CreatorOpportunities';

function Gate() {
  const { loading, signedIn, registered, vendor } = useSession();
  const { pathname } = useLocation();

  // An invitation link works before anyone is signed in on this phone.
  if (pathname.startsWith('/invite/'))
    return (
      <Routes>
        <Route path="/invite/:token" element={<InvitePage />} />
      </Routes>
    );
  if (loading) return <Spinner />;
  if (!signedIn) return <SignInPage />;
  if (!registered) return <RegisterPage />;

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/jobs" element={<JobsPage />} />
        <Route path="/jobs/:jobId/quote" element={<QuotePage />} />
        <Route path="/jobs/:jobId/proof" element={<ProofPage />} />
        <Route path="/listings" element={<ListingsPage />} />
        <Route path="/payouts" element={<PayoutsPage />} />
        <Route path="/bookings" element={<BookingsPage />} />
        <Route path="/orders" element={<OrdersPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/profile/edit" element={<ProfileEditPage />} />
        <Route path="/profile/tax" element={<TaxBankPage />} />
        <Route path="/profile/services" element={<ServicesPage />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/connect" element={<ConnectPage />} />
        <Route path="/creator" element={<CreatorHomePage />} />
        <Route path="/creator/kit" element={<CreatorKitPage />} />
        <Route path="/creator/packages" element={<CreatorPackagesPage />} />
        <Route path="/deals" element={<CreatorDealsPage />} />
        <Route path="/deals/:id" element={<CreatorDealPage />} />
        <Route path="/opportunities" element={<CreatorOpportunitiesPage />} />
        <Route path="/opportunities/:id" element={<CreatorOpportunityPage />} />
        <Route
          path="*"
          element={
            <Navigate
              to={
                vendor && creatorOnly(vendor.capabilities)
                  ? '/creator'
                  : vendor && ownsMedia(vendor.capabilities) && !doesWork(vendor.capabilities)
                    ? '/bookings'
                    : '/jobs'
              }
              replace
            />
          }
        />
      </Route>
    </Routes>
  );
}

export function App() {
  return (
    <I18nProvider>
      <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/$/, '')}>
        <SessionProvider>
          <Gate />
        </SessionProvider>
      </BrowserRouter>
    </I18nProvider>
  );
}
